import logo1 from '../assets/v-rotor-logo.png'

import aero from '../assets/aero.png'
import auto from '../assets/auto.png'
import energy from '../assets/energy.png'
import industry from '../assets/industry.png'
import marine from '../assets/marine.png'
import unman from '../assets/unman.png'

export default function Rotor() {
  return (
    <div className="container">   
      <div className="page">
        <h2 className="align_center">      
          V-Rotor™ &#8212; Rethinking Rotary Power
        </h2>   
        <h4>
          A New Rotary Displacement Architecture
        </h4>              
        <div className="grid-container">
          <div className="grid-item">
            <figure>             
              <img src={logo1} className="logo-big" alt='' />         
            </figure>
          </div>         
          <div className="grid-item">
            <p className='align_left'>
              V-Rotor™ is a new rotary displacement technology designed to combine the compactness and smooth operation of conventional vane-type machines with a fundamentally different rotor and blade architecture.
            </p>
        
          </div>
        </div>
        <p className='align_left'>
          Instead of conventional sliding vanes, V-Rotor uses blades connected directly to the rotating shaft, creating variable-volume working chambers while reducing the relative movement responsible for conventional vane wear.
        </p> 
        <p className='align_left'>
          The objective is simple: more effective rotary displacement. Less mechanical wear.            
        </p>         
        <hr className='line' /> 

        <h3 className="align_center">      
          Moving Beyond the Conventional Vane
        </h3>


        <section className="align_left">
          <p>
            Conventional vane machines are elegant in their simplicity. A rotor, sliding vanes, and housing create continuously changing working chambers that can be used for pumping, compression, expansion, and power generation.          
          </p>

          <p>
            However, the same sliding motion that makes conventional vane systems work also creates significant engineering challenges.          
          </p>

          Continuous contact between the vane and housing can result in: 

          <p className='align_center'>                    
            <ul className='my-ul'>
              <li>Friction and mechanical losses</li>
              <li>Vane and housing wear</li>
              <li>Sealing degradation</li>
              <li>Lubrication requirements</li>
              <li>Internal leakage</li>
              <li>Thermal expansion and dimensional changes</li>
              <li>Increasing maintenance requirements over time</li>
            </ul>                        
          </p>
        </section>
        <b> V-Rotor approaches the problem differently. </b>

        <hr className='line' />       
        <h3 className="align_center">      
          The V-Rotor Concept
        </h3>

        <section className="align_left">
          <p>
            At the heart of V-Rotor is a rotary displacement mechanism consisting of a housing, shaft-connected blades, and variable-volume working chambers.            
          </p>
          <p>
            The blades are mechanically connected to the rotating shaft and rotate with the shaft rather than continuously sliding radially through the rotor.          
          </p>
          <p>
            This architecture is intended to reduce the primary wear mechanism associated with conventional sliding-vane designs while maintaining the fundamental variable-volume displacement principle.          
          </p>


        <h4 className="align_center">      
          Integrated Blade Architecture
        </h4>  
        
        <p><b>Blades connected to the shaft:</b> &nbsp; The blades form an integrated rotating assembly, providing a direct mechanical path for torque transmission.</p>
            
        <p><b>Reduced Sliding Wear:</b> &nbsp; By replacing conventional radially sliding vanes with shaft-connected blades, V-Rotor is designed to substantially reduce relative sliding movement between critical components.</p>

        <p><b>Thermal Management:</b> &nbsp; The architecture is designed to accommodate conventional liquid-cooling approaches, providing a pathway for controlled operating temperatures and management of thermal expansion.</p>

        <p><b>Application-Specific Porting: </b> &nbsp; The inlet and outlet geometry can be configured for the intended application and operating cycle.</p>
        </section>

        <hr className='line' />      

        <h3 className="align_center">      
          One Architecture &#8212; Multiple Applications
        </h3> 
        <section className="align_left">
          <p>
            V-Rotor is being developed as a rotary displacement platform, rather than being limited to a single machine type.             
            The underlying architecture may be adapted for different operating modes through changes to the housing, port configuration, sealing arrangements, and operating conditions.          
          </p>

          <p><b>Engine:</b> &nbsp; A potential internal combustion engine architecture using the V-Rotor variable-volume chambers for intake, compression, combustion, expansion, and exhaust.</p>
          
          <p><b>Pump:</b> &nbsp; A rotary displacement pump for moving liquids or other working fluids.</p>
          
          <p><b>Compressor:</b> &nbsp; A compact rotary compressor configuration for gases and other compressible fluids.</p>
          
          <p><b>Expander:</b> &nbsp; A rotary expansion machine capable of converting fluid pressure or thermal energy into mechanical rotation.</p>

          <p><b>Motor:</b> &nbsp; Potential hydraulic or pneumatic motor configurations using pressurised working fluid to generate rotary torque.</p>
        </section>
        <hr className='line' />       
        <h3 className="align_center">      
          Designed Around the Problems
        </h3>

        <section className="align_left">
          <p>V-Rotor is not simply an attempt to make another rotary engine. 
          The development is focused on addressing specific engineering limitations that have historically constrained rotary displacement technologies. </p>
          
          <p><b>Wear:</b> &nbsp; Reducing continuous sliding contact between the primary rotating elements.</p>

          <p><b>Sealing:</b> &nbsp; Developing an architecture that can maintain effective separation between working chambers while reducing the mechanical demands placed on conventional sliding seals.</p>

          <p><b>Thermal Expansion:</b> &nbsp; Providing a pathway for controlled thermal management and dimensional stability.</p>
          
          <p><b>Lubrication:</b> &nbsp; Allowing lubrication features to be incorporated where required by the particular application, while seeking to minimise lubrication-related losses and complexity.</p>

          <p><b>Manufacturability:</b> &nbsp; Developing a relatively compact mechanical architecture using components that can potentially be manufactured using established engineering processes.</p>
        </section>
        <hr className='line' />      

        <h3 className="align_center">      
          From Concept to Working Machine
        </h3> 

        <section className="align_left">
          <p>V-Rotor is currently progressing through the conceptual design and intellectual-property development stage. The development pathway includes: </p>
          
          <p><b>01 — Architecture</b> &nbsp; Define and refine the rotor, blade, housing, chamber and sealing geometry.</p>

          <p><b>02 — Engineering Analysis</b> &nbsp; Use CAD, FEA, CFD and analytical modelling to evaluate structural, thermal and fluid-dynamic behaviour.</p>

          <p><b>03 — Prototype</b> &nbsp; Build a functional prototype to validate the fundamental mechanical operation.</p>

          <p><b>04 — Testing</b> &nbsp; Measure torque, speed, leakage, friction, temperature, pressure and mechanical efficiency.</p>

          <p><b>05 — Engine Demonstration</b> &nbsp; Develop and test an engine configuration where appropriate.</p>

          <p><b>06 — Independent Validation</b> &nbsp; Subject the technology to independent engineering testing and performance evaluation.</p>        
        </section>
        <hr className='line' />      

        <h3 className="align_center">      
          Why V-Rotor?
        </h3> 

        <p>The objective is not to claim that every limitation of conventional rotary machines has already been solved.  
        The objective is to develop and experimentally determine whether a different mechanical architecture can provide meaningful improvements.      
        V-Rotor is therefore being developed around three principles: </p>
        
        <p><b>Simplify the mechanism.</b> Reduce unnecessary relative motion and mechanical complexity.</p>

        <p><b>Control the environment.</b> Manage temperature, pressure, lubrication and sealing conditions.</p>
        
        <p><b>Validate through engineering.</b> Move from concept → analysis → prototype → measurement.</p>                  

        <hr className='line' />      

        <h3 className="align_center">      
          Potential Applications
        </h3> 

        <p>The compact rotary architecture may ultimately have applications across multiple industries.</p>


        <div className="logo-container">
          <img src={auto} className='logo'   alt='' />
          <p className='logo-left'><b>Automotive:</b> &nbsp; Compact power and range-extender applications. </p>
        </div>
        <div className="logo-container">
          <img src={aero} className='logo'   alt='' />
          <p className='logo-left'><b>Aerospace:</b> &nbsp; Lightweight power generation and auxiliary power systems.</p>
        </div>
        <div className="logo-container">
          <img src={unman} className='logo'   alt='' />
          <p className='logo-left'><b>Unmanned Systems:</b> &nbsp; Compact engines and power units for UAV and robotic platforms.</p>
        </div>
        <div className="logo-container">
          <img src={marine} className='logo'   alt='' />
          <p className='logo-left'><b>Marine:</b> &nbsp; Compact propulsion and onboard power generation.</p>
        </div>
        <div className="logo-container">
          <img src={industry} className='logo'   alt='' />
          <p className='logo-left'><b>Industrial:</b> &nbsp; Pumps, compressors, expanders and distributed power systems.</p>
        </div>
        <div className="logo-container">
          <img src={energy} className='logo'   alt='' />
          <p className='logo-left'><b>Energy:</b> &nbsp; Rotary expansion and power-conversion applications.</p>
        </div>
        
        <hr className='line' />     

        <section className="align_center">
          <table className="no-border">
            <caption><h3> V-Rotor™ vs Conventional Vane Architecture </h3></caption>          
            <tr>
              <th>Conventional Vane Machine</th>
              <th>V-Rotor™ Architecture</th>
            </tr>
            <tr>
              <td>Radially sliding vanes</td>
              <td>Shaft-connected blades</td>
            </tr>
            <tr>
              <td>Continuous sliding contact</td>
              <td>Reduced relative sliding movement</td>
            </tr>
            <tr>
              <td>Vane-tip wear</td>
              <td>Designed to reduce primary vane wear</td>
            </tr>
            <tr>
              <td>Lubrication commonly required</td>
              <td>Lubrication configurable to application</td>
            </tr>
            <tr>
              <td>Thermal expansion can affect clearances</td>
              <td>Designed for controlled thermal management</td>
            </tr>
            <tr>
              <td>Application-specific configurations</td>
              <td>Modular application-specific porting</td>
            </tr>
            <tr>
              <td>Established technology</td>
              <td>New architecture under development</td>
            </tr>                                                        
          </table>    
        </section>
    

        <p><i>The comparison describes design objectives and architectural differences. Actual performance advantages remain subject to prototype testing and independent validation.</i></p>
      
        <hr className='line' />      

        <h3 className="align_center">      
          A New Approach to Rotary Machines
        </h3>

        <p>For more than a century, engineers have explored different ways to achieve rotary displacement.</p>
        <p>V-Rotor takes a familiar principle — variable-volume rotary chambers — and asks a different question: <br />
        <b><i>Can we reduce the complexity and improve the performance of rotary machines by reimagining the fundamental architecture?</i></b></p>
        <p>That question is at the centre of the V-Rotor project.</p>

    </div>
  </div>  
  );
};
